/**
 * GemConstellation — Sprint 4A
 * Tells a city's secret WITHOUT spoiling it: a quiet row of stars, dark from the
 * start, says how many gems a place hides and that they exist — never where.
 * Each gem found (surfaced ≤150 m, cross-trip) lights its star.
 *
 * Deliberately not gamified: no progress bar, no "N/M", no points, no numbers at
 * all — the stars themselves carry the count, and the lit star is the reward.
 * Renders nothing for a city with no authored gems (tolerates Érico's absence).
 *
 * Used in two places:
 *   • the active trip's city view (visible even with nothing found yet)
 *   • Explore's city detail, as a sober teaser
 */
import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { cityGemStatus } from '../utils/postcardManager';
import theme from '../styles/theme';

function Star({ lit }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true"
      style={{ display: 'block', filter: lit ? `drop-shadow(0 0 4px ${theme.terracottaLight})` : 'none', transition: 'filter 600ms ease' }}>
      <path
        d="M12 2.5l2.5 6.0 6.5.5-5 4.2 1.6 6.3L12 16.7 6.4 19.5 8 13.2l-5-4.2 6.5-.5z"
        fill={lit ? theme.terracotta : 'none'}
        stroke={lit ? theme.terracotta : theme.sand}
        strokeWidth={lit ? 0 : 1.4}
        strokeLinejoin="round"
        opacity={lit ? 1 : 0.5}
      />
    </svg>
  );
}

export default function GemConstellation({ country, city, variant = 'trip' }) {
  const { t } = useTranslation('common');
  const [status, setStatus] = useState(null);

  const refresh = useCallback(() => {
    let cancelled = false;
    if (!country || !city) { setStatus(null); return () => {}; }
    (async () => {
      const s = await cityGemStatus(country, city).catch(() => null);
      if (!cancelled) setStatus(s);
    })();
    return () => { cancelled = true; };
  }, [country, city]);

  useEffect(() => refresh(), [refresh]);

  // Re-light when a gem surfaces live during the trip.
  useEffect(() => {
    const onSurfaced = () => refresh();
    window.addEventListener('wayfare-gem-surfaced', onSurfaced);
    window.addEventListener('wayfare-postcard-unlocked', onSurfaced);
    return () => {
      window.removeEventListener('wayfare-gem-surfaced', onSurfaced);
      window.removeEventListener('wayfare-postcard-unlocked', onSurfaced);
    };
  }, [refresh]);

  // No gems authored for this city → say nothing.
  if (!status || status.total === 0) return null;

  const { total, found, complete } = status;
  const stars = Array.from({ length: total });

  return (
    <div style={{ ...S.wrap, ...(variant === 'explore' ? S.wrapExplore : null) }}>
      <div style={S.eyebrow}>{t('constellation.eyebrow')}</div>
      <div style={S.stars} role="img"
        aria-label={t('constellation.aria', { found, total })}>
        {stars.map((_, i) => <Star key={i} lit={i < found} />)}
      </div>
      <p style={S.caption}>
        {complete ? t('constellation.complete') : t('constellation.teaser')}
      </p>
    </div>
  );
}

const S = {
  wrap: {
    background: theme.cream,
    border: `1px solid ${theme.creamDeep}`,
    borderRadius: 14,
    padding: '16px 18px',
    margin: '12px 0',
    textAlign: 'center',
  },
  wrapExplore: {
    background: 'rgba(255,255,255,0.6)',
    border: `1px solid ${theme.bluePale}`,
  },
  eyebrow: {
    fontSize: 11,
    letterSpacing: '0.1em',
    textTransform: 'uppercase',
    color: theme.blue,
    fontWeight: 600,
    marginBottom: 10,
  },
  stars: {
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    gap: 8,
    marginBottom: 10,
  },
  caption: {
    margin: 0,
    fontSize: 13.5,
    lineHeight: 1.5,
    color: theme.brown,
    fontStyle: 'italic',
  },
};
